import { useAuth0 } from '#~/auth/index.js';
import { HRule } from '#~/video/components/settings/layout.js';
import { Button } from '@studio/design-system/button';
import { Label } from '@studio/design-system/label';
import { usePrompt } from '@studio/design-system/prompt';
import * as Select from '@studio/design-system/select';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
// import { useMount } from '@studio/react-utils/hooks';
import type { StatusDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useCallback, useRef, useState } from 'react';
import { DestinationsGrid } from './destinations-grid.js';
import { ChromeCastIcon } from './icons.js';
import { DestinationFooter } from './panel/destination/footer.js';
import { DestinationPanel } from './panel/destination/panel.js';
import type { Destination } from './store/destinations.js';
import {
  useAddDestination,
  useDestinationsWithGlobalSync,
  useRemoveDestination,
  useUpdateDestination,
  useUpdateProfile
} from './store/destinations.js';
import { DEFAULT_PROFILES, StreamProfileSelect } from './stream-profile-select.js';
import { getProfileConfig } from './utils.js';
import { generateId } from '@studio/models';

interface RTMP_V2Props {
  agentId: T_Maybe<Agent['id']>;
  selectedVideoDevice: T_Maybe<Device['id']>;
  videoSources: {
    id: Device['id'];
    name: string;
    egress: StatusDevice['egress'];
    type: 'video' | 'audio';
    videoCodec: string | undefined;
  }[];
  hasChanges: boolean;
}

export const RTMP_V2 = ({
  agentId,
  selectedVideoDevice,
  videoSources,
  hasChanges
}: RTMP_V2Props) => {
  const [streamProfile, setStreamProfile] = useState(DEFAULT_PROFILES[0]?.value);
  const [isDestinationPanelOpen, setIsDestinationPanelOpen] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [editingDestination, setEditingDestination] = useState<Destination | null>(null);
  const [additionMode, setAdditionMode] = useState(false);

  const { getAccessTokenSilently } = useAuth0();

  // Initial values for change detection
  const initialStreamProfile = useRef(DEFAULT_PROFILES[0]?.value);
  const initialFormState = useRef({
    nickname: '',
    server: '',
    streamKey: '',
    requireAuth: false,
    credentials: undefined as M_Credentials | undefined
  });

  // ####### Form state for DestinationPanel #######
  const [nickname, setNickname] = useState('');
  const [server, setServer] = useState('');
  const [streamKey, setStreamKey] = useState('');
  const [requireAuth, setRequireAuth] = useState(false);
  const [credentials, setCredentials] = useState<M_Credentials | undefined>(undefined);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // ####### Form state for DestinationPanel #######

  // ####### Destinations management #######
  const destinations = useDestinationsWithGlobalSync();
  const addDestination = useAddDestination();
  const removeDestination = useRemoveDestination();
  const updateProfile = useUpdateProfile();
  const updateDestination = useUpdateDestination();
  const prompt = usePrompt();

  // Check if we have any RTMP destinations
  const hasDestinations = destinations.length > 0;

  // Find the selected profile object
  const selectedProfile = DEFAULT_PROFILES.find(
    (p: { label: string; value: string }) => p.value === streamProfile
  );

  const handleOpenDestinationPanel = () => {
    setEditingDestination(null);
    // Reset initial form state for new destination
    initialFormState.current = {
      nickname: '',
      server: '',
      streamKey: '',
      requireAuth: false,
      credentials: undefined
    };
    setAdditionMode(true);
    setIsDestinationPanelOpen(true);
  };

  const handleEditDestination = useCallback(async (destination: Destination) => {
    setAdditionMode(false);

    setEditingDestination(destination);
    setNickname(destination.nickName);
    setServer(destination.url);
    setStreamKey(destination.streamKey);
    setRequireAuth(destination.requireAuth);
    setCredentials(destination.credentials);

    // Update initial form state for change detection when editing
    initialFormState.current = {
      nickname: destination.nickName,
      server: destination.url,
      streamKey: destination.streamKey,
      requireAuth: destination.requireAuth,
      credentials: destination.credentials
    };

    // Open the `DestinationPanel` slideout
    setIsDestinationPanelOpen(true);
  }, []);

  // Track if the profile has changed
  const handleStreamProfileChange = (value: string) => {
    setStreamProfile(value);
  };

  // TODO: clean -- the stream profile logic is going to have to be updated; after the BE changes.
  // Need to sync up with Alejandro and Noah on this ASAP.
  const handleSaveProfile = useCallback(async () => {
    if (!selectedProfile || !selectedVideoDevice) {
      prompt.show({
        title: 'Error',
        message: 'Please select both a stream profile and a video device.',
        okay: { label: 'OK' }
      });
      return;
    }

    setIsUpdatingProfile(true);
    try {
      const profileConfig = getProfileConfig(selectedProfile.value, selectedVideoDevice);
      await updateProfile(profileConfig);
      prompt.show({
        title: 'Success',
        message: 'Stream profile updated successfully.',
        okay: { label: 'OK' }
      });
    } catch (error) {
      console.error('Failed to update profile:', error);
      prompt.show({
        title: 'Error',
        message: 'Failed to update stream profile. Please try again.',
        okay: { label: 'OK' }
      });
    } finally {
      setIsUpdatingProfile(false);
    }
  }, [selectedProfile, selectedVideoDevice, updateProfile, prompt]);

  // Form validation logic
  const validateForm = useCallback(() => {
    const validationRules = [
      {
        field: 'nickname',
        value: nickname,
        validate: (value: string) => value.trim().length > 0,
        message: 'Destination nickname is required'
      },
      {
        field: 'server',
        value: server,
        validate: (value: string) => {
          const trimmed = value.trim();
          return (
            trimmed.length > 0 && (trimmed.startsWith('rtmp://') || trimmed.startsWith('rtmps://'))
          );
        },
        message: 'Server URL must start with rtmp:// or rtmps://'
      },
      {
        field: 'streamKey',
        value: streamKey,
        validate: (value: string) => value.trim().length > 0,
        message: 'Stream key is required'
      }
    ];

    const newErrors = validationRules.reduce(
      (errors, rule) => {
        if (!rule.validate(rule.value)) {
          errors[rule.field] = rule.message;
        }
        return errors;
      },
      {} as Record<string, string>
    );

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [nickname, server, streamKey]);

  const resetForm = () => {
    setNickname('');
    setServer('');
    setStreamKey('');
    setRequireAuth(false);
    setCredentials(undefined);
    setErrors({});
    setEditingDestination(null);
  };

  const handleDestinationAdd = useCallback(async () => {
    if (!validateForm()) {
      setErrors((prev) => ({ ...prev, general: 'Please fill all required fields.' }));
      return;
    }

    const destination: Destination = {
      id: `${generateId()}_destination_${selectedVideoDevice}`,
      nickName: nickname,
      url: server,
      streamKey,
      requireAuth,
      credentials: requireAuth ? (credentials ?? {}) : ({} as M_Credentials)
    };

    try {
      const token = await getAccessTokenSilently();
      await addDestination(destination, agentId, selectedVideoDevice, token);
      resetForm();
      setIsDestinationPanelOpen(false);
    } catch (error) {
      console.error('Failed to add destination:', error);
      prompt.show({
        title: 'Error',
        message: 'Failed to add destination. Please try again.',
        okay: { label: 'OK' }
      });
    }
  }, [nickname, server, streamKey, requireAuth, credentials, addDestination, validateForm, prompt]);

  const handleDestinationDelete = useCallback(async () => {
    if (!editingDestination) return;

    prompt.confirm({
      title: 'Delete Destination',
      message: `Are you sure you want to delete ${editingDestination.nickName}? This action cannot be undone.`,
      okay: {
        variant: 'danger',
        label: 'Delete Destination'
      },
      async onConfirm(confirmed) {
        if (!confirmed || !editingDestination) return;

        try {
          const token = await getAccessTokenSilently();
          await removeDestination(editingDestination, agentId, selectedVideoDevice, token);
          setIsDestinationPanelOpen(false);

          resetForm();
        } catch (error) {
          console.error('Failed to delete destination:', error);
          prompt.show({
            title: 'Error',
            message: 'Failed to delete destination. Please try again.',
            okay: { label: 'OK' }
          });
        }
      }
    });
  }, [editingDestination, removeDestination, selectedVideoDevice, prompt]);

  const handleDestinationEdit = useCallback(async () => {
    if (!editingDestination) return;

    // Validate form before proceeding
    if (!validateForm()) {
      setErrors((prev) => ({ ...prev, general: 'Please fill all required fields.' }));
      return;
    }

    // Use current form state values, not editingDestination values
    const updates = {
      url: server,
      streamKey: streamKey,
      nickName: nickname
    };

    try {
      const token = await getAccessTokenSilently();
      await updateDestination(editingDestination.id, agentId, selectedVideoDevice, token, updates);

      resetForm();
      setIsDestinationPanelOpen(false);

      prompt.show({
        title: 'Success',
        message: 'Destination updated successfully.',
        okay: { label: 'OK' }
      });
    } catch (error) {
      console.error('Failed to update destination:', error);
      prompt.show({
        title: 'Error',
        message: 'Failed to update destination. Please try again.',
        okay: { label: 'OK' }
      });
    }
  }, [
    editingDestination,
    selectedVideoDevice,
    nickname,
    server,
    streamKey,
    requireAuth,
    credentials,
    updateDestination,
    validateForm,
    prompt
  ]);

  const handleClose = useCallback(() => {
    setIsDestinationPanelOpen(false);
  }, []);

  // Detect local changes by comparing current state with initial values
  const hasLocalChanges =
    streamProfile !== initialStreamProfile.current ||
    nickname !== initialFormState.current.nickname ||
    server !== initialFormState.current.server ||
    streamKey !== initialFormState.current.streamKey ||
    requireAuth !== initialFormState.current.requireAuth ||
    credentials !== initialFormState.current.credentials;

  // Combine external changes prop with local form changes
  const hasAnyChanges = hasChanges || hasLocalChanges;

  return (
    <div className='flex h-full w-full flex-col px-8 pt-8 text-white'>
      {/* Header */}
      <div className='mt-12'>
        <div className='text-base font-demibold text-[#F7F7F7]'>Real-Time Messaging Protocol</div>
        <div className='mt-8 text-sm font-medium text-[#ACB4C2]'>
          Configure video ingestion from your camera into the Studio.
        </div>
      </div>

      <HRule className='my-10' />

      {/* Stream Profile Selector */}
      <div className='mt-12 flex items-center gap-16' data-stag='rtmp.stream_profile.section'>
        <div className='min-w-[220px] flex-col'>
          <Label className='mb-1 py-6 text-sm font-medium leading-[12px] text-light'>
            Stream Profile
          </Label>
          <Select.Root
            value={streamProfile}
            onValueChange={handleStreamProfileChange}
            size='md'
            variant='dark'
            data-stag='rtmp.stream_profile.selector'
          >
            <Select.Trigger className='w-[368px] bg-base-100'>
              <Select.Value placeholder='Select a stream profile' />
            </Select.Trigger>
            <StreamProfileSelect />
          </Select.Root>
        </div>
        <div className='mt-24'>
          <Button
            size='md'
            variant='secondary'
            onClick={handleSaveProfile}
            disabled={isUpdatingProfile || streamProfile === DEFAULT_PROFILES[0]!.value}
            aria-label='Save selected stream profile as destination'
            data-stag='rtmp.stream_profile.save_button'
          >
            {isUpdatingProfile ? 'Updating...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Destinations Section */}
      <div className='mt-32' data-stag='rtmp.destinations.section'>
        <div className='flex items-center justify-between'>
          <span className='text-base font-demibold' data-stag='rtmp.destinations.header'>
            Destinations{' '}
            <span className='font-normal text-base text-[#758599]'>(Add up to 25)</span>
          </span>
          <Button
            size='md'
            variant='primary'
            onClick={handleOpenDestinationPanel}
            data-stag='rtmp.destinations.add_button'
          >
            Add Destination
          </Button>
        </div>
        <HRule className='my-10' />
        {!hasDestinations ? (
          <div
            className='mt-[10%] flex h-full w-full flex-col items-center justify-center p-16'
            data-stag='rtmp.destinations.empty_state'
          >
            <ChromeCastIcon />
            <p className='w-[47%] text-center text-base font-regular text-[#607090]'>
              You currently have no RTMP streaming destinations defined. Click{' '}
              <span className='font-medium text-[#3664FF]'>"Add Destination"</span> to get started.
            </p>
          </div>
        ) : (
          <DestinationsGrid
            destinations={destinations}
            videoSources={videoSources}
            onEditDestination={handleEditDestination}
            streamProfile={streamProfile || undefined}
            data-stag='rtmp.destinations.grid'
          />
        )}
      </div>

      <DestinationPanel
        isOpen={isDestinationPanelOpen}
        onClose={handleClose}
        nickname={nickname}
        setNickname={setNickname}
        server={server}
        setServer={setServer}
        streamKey={streamKey}
        setStreamKey={setStreamKey}
        requireAuth={requireAuth}
        setRequireAuth={setRequireAuth}
        credentials={credentials}
        setCredentials={setCredentials}
        errors={errors}
        data-stag='rtmp.destinations.panel'
        FooterComponent={
          <DestinationFooter
            hasChanges={hasAnyChanges}
            onClose={handleClose}
            onDestinationAdd={handleDestinationAdd}
            onDestinationDelete={handleDestinationDelete}
            onDestinationEdit={handleDestinationEdit}
            isEditMode={!!editingDestination}
            isAdditionMode={additionMode}
            data-stag='rtmp.destinations.panel.footer'
          />
        }
      />
    </div>
  );
};
